import { useCallback, useEffect, useRef, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { IconButton } from '../../design/primitives/Button';
import { Icon } from '../../design/icons';
import { useWindows, type WindowState } from './WindowManager';

// One floating window: a draggable title bar, resize handles (edges + SE corner), min/max/
// close, a focus ring and z-index bump on focus. Pointer events only — no dependency. Motion
// is CSS/token-driven (reduced-motion aware). Constrained so the title bar stays on-screen.

const MENUBAR_H = 34;
const TASKBAR_H = 40;
/** Window z-index band: above the docked inspector (40), below every overlay (drawer 120+). */
export const WINDOW_Z_BASE = 100;
export const WINDOW_Z_MAX = 119;
const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function Window({ win, focused, children }: { win: WindowState; focused: boolean; children: ReactNode }) {
  const wm = useWindows();
  const ref = useRef<HTMLDivElement>(null);
  const drag = useRef<{ dx: number; dy: number } | null>(null);
  const sizing = useRef<{ dir: string; sx: number; sy: number; sw: number; sh: number; ox: number; oy: number } | null>(null);

  // The title bar follows the content: whatever page heading the window's screen renders
  // (a list title, a record's name) becomes the window title once it appears or changes.
  const setTitle = wm.setTitle; // stable callback — the effect must not depend on the whole manager
  useEffect(() => {
    const body = ref.current?.querySelector('.hf-window-body');
    if (!body) return;
    const sync = () => {
      const t = body.querySelector('h1')?.textContent?.trim();
      if (t) setTitle(win.id, t);
    };
    sync();
    const mo = new MutationObserver(sync);
    mo.observe(body, { childList: true, subtree: true, characterData: true });
    return () => mo.disconnect();
  }, [setTitle, win.id, win.path]);

  const vw = () => window.innerWidth;
  const vh = () => window.innerHeight;

  const onTitlePointerDown = useCallback(
    (e: PointerEvent) => {
      if (win.maximized) return;
      if ((e.target as HTMLElement).closest('button')) return;
      e.preventDefault();
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      drag.current = { dx: e.clientX - win.x, dy: e.clientY - win.y };
      wm.focus(win.id);
    },
    [win.maximized, win.x, win.y, win.id, wm],
  );

  const onTitlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (!drag.current) return;
      const x = Math.max(0, Math.min(e.clientX - drag.current.dx, vw() - win.w));
      const y = Math.max(MENUBAR_H, Math.min(e.clientY - drag.current.dy, vh() - TASKBAR_H - 24));
      wm.move(win.id, x, y);
    },
    [win.w, win.id, wm],
  );

  const endDrag = useCallback((e: PointerEvent) => {
    drag.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  const onResizePointerDown = (dir: string) => (e: PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    sizing.current = { dir, sx: e.clientX, sy: e.clientY, sw: win.w, sh: win.h, ox: win.x, oy: win.y };
    wm.focus(win.id);
  };
  const onResizePointerMove = (e: PointerEvent) => {
    const s = sizing.current;
    if (!s) return;
    const dx = e.clientX - s.sx;
    const dy = e.clientY - s.sy;
    let { sw: w, sh: h, ox: x, oy: y } = s;
    if (s.dir.includes('e')) w = s.sw + dx;
    if (s.dir.includes('s')) h = s.sh + dy;
    if (s.dir.includes('w')) {
      w = s.sw - dx;
      x = s.ox + dx;
    }
    if (s.dir.includes('n')) {
      h = s.sh - dy;
      y = s.oy + dy;
    }
    wm.resize(win.id, w, h, s.dir.includes('w') ? x : undefined, s.dir.includes('n') ? Math.max(MENUBAR_H, y) : undefined);
  };
  const endResize = (e: PointerEvent) => {
    sizing.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  // Overlays (Dialog / Drawer / Popover / DropdownMenu) portal to <body> but React still bubbles
  // their events through this component: anything whose DOM target is not inside the window's
  // own subtree is theirs, not ours — Esc must reach their document listener and a click in a
  // dialog must not re-focus (re-stack) the window underneath it.
  const ownsEvent = (target: EventTarget | null) => Boolean(ref.current && target instanceof Node && ref.current.contains(target));

  // Keep Tab within the focused window; Esc drops focus back to the desktop.
  const onKeyDown = (e: KeyboardEvent) => {
    if (!ownsEvent(e.target)) return;
    if (e.key === 'Escape') {
      e.stopPropagation();
      (document.activeElement as HTMLElement | null)?.blur?.();
      return;
    }
    if (e.key !== 'Tab' || !ref.current) return;
    const items = Array.from(ref.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (items.length === 0) return;
    const first = items[0]!;
    const last = items[items.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  // Windows stack between the inspector (--z-inspector 40) and the drawer (--z-drawer 120): the
  // manager keeps `z` compact (1..n), and the cap keeps a pathological count from ever climbing
  // over the overlay ladder in tokens.css.
  const zIndex = Math.min(WINDOW_Z_BASE + win.z, WINDOW_Z_MAX);
  const style = win.maximized
    ? { left: 0, right: 0, top: MENUBAR_H, bottom: TASKBAR_H, width: 'auto', height: 'auto', zIndex }
    : { left: win.x, top: win.y, width: win.w, height: win.h, zIndex };

  return (
    <div
      ref={ref}
      className={['hf-window', focused && 'focused', win.maximized && 'maximized'].filter(Boolean).join(' ')}
      style={style}
      role="dialog"
      aria-label={win.title}
      onKeyDown={onKeyDown}
      onPointerDown={(e) => {
        if (!ownsEvent(e.target) || focused) return;
        wm.focus(win.id);
      }}
      hidden={win.minimized}
    >
      <div className="hf-window-bar" onPointerDown={onTitlePointerDown} onPointerMove={onTitlePointerMove} onPointerUp={endDrag} onDoubleClick={() => wm.toggleMax(win.id)}>
        <Icon name="app-window" size={13} className="hf-window-ico" />
        {(win.index ?? 0) > 0 && <IconButton icon="arrow-left" label="Back" size="sm" onClick={() => wm.go(win.id, -1)} />}
        <span className="hf-window-title">{win.title}</span>
        <div className="hf-window-controls">
          <IconButton icon="minus" label="Minimize" size="sm" onClick={() => wm.minimize(win.id)} />
          <IconButton icon={win.maximized ? 'minimize' : 'maximize'} label={win.maximized ? 'Restore' : 'Maximize'} size="sm" onClick={() => wm.toggleMax(win.id)} />
          <IconButton icon="x" label="Close" size="sm" onClick={() => wm.close(win.id)} />
        </div>
      </div>
      <div className="hf-window-body hf-scroll">{children}</div>
      {!win.maximized && (
        <>
          <span className="hf-window-rz e" onPointerDown={onResizePointerDown('e')} onPointerMove={onResizePointerMove} onPointerUp={endResize} aria-hidden />
          <span className="hf-window-rz s" onPointerDown={onResizePointerDown('s')} onPointerMove={onResizePointerMove} onPointerUp={endResize} aria-hidden />
          <span className="hf-window-rz se" onPointerDown={onResizePointerDown('se')} onPointerMove={onResizePointerMove} onPointerUp={endResize} aria-hidden />
        </>
      )}
    </div>
  );
}
